import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, X, Bot, CheckCircle2, AlertTriangle, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAsaPersonality } from '@/hooks/useAsaPersonality';
import { useSession } from '@/lib/SessionContext';
import { base44 } from '@/api/base44Client';

export default function AsaAssistant({ 
  insights = [], 
  memberName = 'Tim', 
  title = "Asa AI Assistant",
  theme = "default" // default (marketing style), indigo (purchasing style)
}) {
  const [dismissedState, setDismissedState] = useState({});
  const [now, setNow] = useState(Date.now());
  const [showAll, setShowAll] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { member } = useSession();
  
  // Use centralized personality hook
  const { sapaan, gaya, isCustom } = useAsaPersonality();
  const effectiveSapaan = isCustom ? sapaan : memberName;

  // Load from database on mount (fallback: localStorage for migration)
  useEffect(() => {
    if (!member) return;
    
    const loadDismissed = async () => {
      try {
        // Try loading from member preferences in DB first
        const fresh = await base44.entities.TeamMember.get(member.id);
        if (fresh?.asa_dismissed_insights) {
          const parsed = typeof fresh.asa_dismissed_insights === 'string' 
            ? JSON.parse(fresh.asa_dismissed_insights) 
            : fresh.asa_dismissed_insights;
          setDismissedState(parsed);
        } else {
          setDismissedState({});
        }
      } catch (e) {
        console.error("Could not load Asa memory", e);
      }
    };
    loadDismissed();
    
    // Update 'now' every minute so relative times update
    const interval = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(interval);
  }, [member]);

  const saveDismissed = useCallback(async (newState) => {
    setDismissedState(newState);
    if (!member) return;
    try {
      await base44.entities.TeamMember.update(member.id, {
        asa_dismissed_insights: JSON.stringify(newState)
      });
    } catch (e) {
      console.error("Could not save Asa memory to DB", e);
    }
  }, [member]);

  const handleDismiss = (insightId, currentLevel) => {
    const newState = { ...dismissedState };
    const currentRecord = newState[insightId] || { count: 0, lastDismissed: 0, ignoredForever: false };
    
    newState[insightId] = {
      ...currentRecord,
      count: currentRecord.count + 1,
      lastDismissed: Date.now()
    };
    
    saveDismissed(newState);
  };

  const handleIgnoreForever = (insightId) => {
    const newState = { ...dismissedState };
    newState[insightId] = {
      ...newState[insightId],
      ignoredForever: true,
      lastDismissed: Date.now()
    };
    saveDismissed(newState);
  };

  // Process insights based on memory
  const processedInsights = insights.map((insight, index) => {
    // Generate a unique ID if not provided, based on text content and index to prevent collision
    const id = insight.id || (btoa(encodeURIComponent(String(insight.text || ''))).slice(0, 32) + '-' + index);
    const record = dismissedState[id] || { count: 0, lastDismissed: 0, ignoredForever: false };
    
    if (record.ignoredForever) return null; // Fully ignored
    
    // Calculate hours since last dismissed
    const hoursSinceDismissed = (now - record.lastDismissed) / (1000 * 60 * 60);
    
    // If dismissed recently (under 48 hours), hide it
    if (record.count > 0 && hoursSinceDismissed < 48) {
      return null;
    }
    
    let level = 1; // Normal
    if (record.count === 1) level = 2; // Annoyed
    if (record.count >= 2) level = 3; // Angry / Asking for ignore

    return { ...insight, _id: id, _level: level, _record: record };
  }).filter(Boolean);

  // If no insights to show after filtering
  if (processedInsights.length === 0) {
    if (theme === 'indigo') {
       return (
        <div className="text-center py-10 bg-white border border-slate-200 rounded-3xl">
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 border border-emerald-200 overflow-hidden shadow-sm mb-3">
            <img src="/asa-profile.png?v=3" alt="Asa Profile" className="w-full h-full object-cover" />
          </div>
          <p className="text-sm font-semibold text-slate-500">Semua aman & terkendali!</p>
          <p className="text-xs text-slate-400 mt-1">Asa belum menemukan hal *urgent* untuk dilaporkan.</p>
        </div>
       );
    }
    
    return (
      <Card className="border-[#E6D5B8]/40 shadow-sm bg-gradient-to-br from-[#FFFDF3] to-[#FFFFFF] rounded-xl md:rounded-3xl overflow-hidden relative">
        <CardContent className="p-2 md:p-8 flex items-center justify-between">
           <div className="flex items-center gap-1.5 md:gap-4">
              <div className="w-6 h-6 md:w-12 md:h-12 rounded-full bg-emerald-50 flex items-center justify-center border border-emerald-200 shrink-0 overflow-hidden shadow-sm">
                <img src="/asa-profile.png?v=3" alt="Asa Profile" className="w-full h-full object-cover" />
              </div>
              <div className="min-w-0">
                <h3 className="text-[9px] md:text-base font-bold text-emerald-800">Semua Terkendali!</h3>
                <p className="text-[6.5px] md:text-sm text-emerald-600/80 mt-0 md:mt-0.5 leading-[1.2]">Asa tidak menemukan masalah atau notifikasi penting saat ini.</p>
              </div>
           </div>
        </CardContent>
      </Card>
    );
  }

  // Theme styling
  const isIndigo = theme === 'indigo';
  const bgClass = isIndigo ? "bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-100" : "bg-gradient-to-br from-[#FFFDF3] to-[#FFFFFF] border-[#E6D5B8]/40";
  const iconBg = isIndigo ? "bg-white text-indigo-600" : "bg-gradient-to-br from-[#D4AF37] to-[#8C6208] text-white shadow-lg shadow-[#D4AF37]/20 border-4 border-white";
  const titleColor = isIndigo ? "text-indigo-900" : "text-[#8C6208]";
  const badgeClass = isIndigo ? "bg-indigo-600 text-white" : "bg-amber-100 text-amber-700";

  return (
    <Card className={`border shadow-sm rounded-xl md:rounded-3xl overflow-hidden relative ${bgClass}`}>
      {!isIndigo && (
        <div className="absolute top-0 right-0 p-4 md:p-8 opacity-[0.03] pointer-events-none">
          <Sparkles className="w-16 h-16 md:w-48 md:h-48" />
        </div>
      )}
      <CardContent className="p-2 md:p-8 flex flex-col sm:flex-row gap-2 md:gap-6 items-start relative z-10">
        <div className={`w-8 h-8 md:w-16 md:h-16 rounded-full flex items-center justify-center shrink-0 overflow-hidden border border-amber-200 shadow-sm ${iconBg}`}>
          <img src="/asa-profile.png?v=3" alt="Asa Profile" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 w-full min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className={`text-[10px] md:text-lg font-black flex items-center gap-1.5 md:gap-2 ${titleColor}`}>
              {title}
              <span className={`px-1.5 py-0.5 text-[7px] md:text-[9px] uppercase tracking-widest rounded-full font-bold ${badgeClass}`}>
                {processedInsights.length} Analisis Aktif
              </span>
            </h2>

            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[10px] md:text-xs text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              {isCollapsed ? "Buka Pengingat ▼" : "Minimalkan ▲"}
            </Button>
          </div>

          {!isCollapsed && (
            <div className="space-y-3 mt-4">
              {(showAll ? processedInsights : processedInsights.slice(0, 2)).map((insight, idx) => {
                const level = insight._level;
                
                let alertBoxClass = 'bg-blue-50 border-blue-200 text-blue-800';
                let Icon = Sparkles;
                let iconColor = 'text-blue-500';

                if (insight.type === 'alert') {
                  alertBoxClass = 'bg-rose-50 border-rose-200 text-rose-800';
                  Icon = AlertCircle;
                  iconColor = 'text-rose-500';
                } else if (insight.type === 'warning') {
                  alertBoxClass = 'bg-amber-50 border-amber-200 text-amber-800';
                  Icon = AlertTriangle;
                  iconColor = 'text-amber-500';
                }

                // Escalation Styles
                if (level === 2) {
                  alertBoxClass = 'bg-orange-50 border-orange-300 text-orange-900 shadow-sm';
                  Icon = AlertCircle;
                  iconColor = 'text-orange-600 animate-pulse';
                } else if (level === 3) {
                  alertBoxClass = 'bg-red-50 border-red-300 text-red-900 shadow-md ring-1 ring-red-200';
                  Icon = AlertTriangle;
                  iconColor = 'text-red-600';
                }

                return (
                  <div key={insight._id} className="relative group">
                    <div className={`rounded-lg md:rounded-xl p-2.5 md:p-4 border flex flex-col sm:flex-row items-start gap-1.5 md:gap-3 transition-all duration-300 pr-6 md:pr-4 ${alertBoxClass}`}>
                      
                      <Icon className={`w-3.5 h-3.5 md:w-5 md:h-5 shrink-0 mt-0.5 ${iconColor}`} />
                      
                      <div className="flex-1 space-y-1.5">
                        {/* Escalation Header Text */}
                        {level === 2 && (
                          <p className="text-xs font-black uppercase tracking-wider text-orange-600">
                            Kemarin kamu tutup pengingat ini... Aku ingetin lagi nih! 😠
                          </p>
                        )}
                        {level === 3 && (
                          <p className="text-xs font-black uppercase tracking-wider text-red-600">
                            Ini sudah ke-3 kalinya aku ingetin tapi belum beres juga! 😡
                          </p>
                        )}

                        {/* Main Insight Text */}
                        <div 
                          className="text-[8px] md:text-sm font-medium leading-[1.3] md:leading-relaxed" 
                          dangerouslySetInnerHTML={{ 
                            __html: (idx === 0 
                              ? (gaya === 'formal' ? `Mohon perhatian ${effectiveSapaan}, ` : `Halo ${effectiveSapaan}! `) 
                              : '') + String(insight.text || '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\*(.*?)\*/g, '<em>$1</em>') 
                          }} 
                        />

                        {/* Escalation Options */}
                        {level === 3 && (
                          <div className="mt-3 pt-3 border-t border-red-200 flex flex-col gap-2">
                            <p className="text-xs font-semibold text-red-800">
                              Apakah data ini memang jarang digunakan / tidak akan berubah?
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-xs border-red-300 text-red-700 hover:bg-red-100 h-8"
                                onClick={() => handleIgnoreForever(insight._id)}
                              >
                                Ya, Abaikan Selamanya
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-xs text-red-700 hover:bg-red-100 h-8"
                                onClick={() => handleDismiss(insight._id, level)}
                              >
                                Nanti Saja (Tutup 2 Hari)
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Dismiss Button (Only for level 1 and 2, level 3 has custom buttons) */}
                    {level < 3 && (
                      <button 
                        onClick={() => handleDismiss(insight._id, level)}
                        className="absolute top-2 right-2 p-1.5 text-slate-400 hover:text-slate-700 hover:bg-white rounded-full bg-white/50 shadow-sm transition-all"
                        title="Tutup (Sembunyikan 48 Jam)"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}

              {/* Show more/less toggle button */}
              {processedInsights.length > 2 && (
                <div className="text-center pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-7 px-3 bg-white/80 hover:bg-white border-amber-200 text-slate-700 shadow-2xs font-semibold"
                    onClick={() => setShowAll(!showAll)}
                  >
                    {showAll ? "Tampilkan Lebih Sedikit ▲" : `Lihat ${processedInsights.length - 2} Rekomendasi Lainnya ▼`}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
